import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";

import { IntelligemsAppScopeList } from "@intelligems/ig-types/schemas/intelligems";
import { useHasRequiredShopifyScopes } from "@src/components/LimitAccess/hooks";
import { useDialogs } from "@src/hooks/dialog";
import { useSelector } from "@src/store";

export const useMaybeShowMissingShopifyScopesDialogAtLogin = () => {
  const snoozeShopifyScopesDialog = useSelector(
    (state) => state.appSettings.snoozeShopifyScopesDialog
  );
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes([
    ...IntelligemsAppScopeList,
  ]);
  const { closeDialog, openDialog } = useDialogs();

  useEffect(() => {
    if (hasRequiredShopifyScopes !== undefined) {
      if (hasRequiredShopifyScopes) {
        closeDialog("missingShopifyScope");
      } else {
        if (
          !snoozeShopifyScopesDialog ||
          (snoozeShopifyScopesDialog &&
            DateTime.fromSeconds(snoozeShopifyScopesDialog).diffNow("days")
              .days > 2)
        ) {
          openDialog("missingShopifyScope", { initial: true });
        } else {
          closeDialog("missingShopifyScope");
        }
      }
    }
  }, [hasRequiredShopifyScopes]);
};

export const useLazyUpdateValue = (
  value: string,
  onChange: (value: string) => void,
  time = 500
) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, time);

    return () => {
      clearTimeout(timeout);
    };
  }, [value]);
};

export const useElement = <T extends Element>() => {
  const [element, setElement] = useState<T | undefined>(undefined);
  const ref = (el: T) => {
    if (el !== element) {
      setTimeout(() => setElement(el), 0);
    }
  };
  return { ref, element };
};

export const useOnResize = <T extends HTMLElement>(
  onResize: (el: T) => void
) => {
  const data = useRef<{ el: T; observer: ResizeObserver }>(undefined);
  return (el: T) => {
    if (el !== data.current?.el) {
      data.current?.observer.disconnect();
      if (el) {
        const observer = new ResizeObserver(() => onResize(el));
        observer.observe(el);
        onResize(el);
        data.current = { el, observer };
      } else {
        data.current = undefined;
      }
    }
  };
};
